import { getMessage } from '@tcc/i18n';
import { CROP_WATER_KEY } from '../../../constants/mapLayerIds';
import { getLayerDatesBySeason } from './common';
import { hasCropWaterUse } from '../../productAssets';
import { createUnit, inferUnit } from '../../../i18n/unitQtyWrapper';
import { LOCALE_GROUPS } from '../../../constants/units';
import { sortDatesDescending } from '../../date';

const PREVIEW_TYPE = 'preview';
const FULL_SIZE_TYPE = 'full-size';

/**
 * Parses the Mosaic event in to a simplified object for the UI to access.
 * @param {Object} event the event
 * @return {Object}
 */
export function parseCropWaterUseEvent(event) {
  const { payload } = event;
  const thumbnail = payload
    .filter((image) => image.meta.type === PREVIEW_TYPE)
    .shift();
  const fullSize = payload
    .filter((image) => image.meta.type === FULL_SIZE_TYPE)
    .shift();
  return {
    date: event.eventDate,
    avgCropWaterUse: event.meta.avgCropWaterUse,
    legend: event.meta.legend,
    thumbnail: thumbnail.uri,
    fullSize: {
      uri: fullSize.uri,
      bbox: fullSize.meta.bbox,
    },
  };
}

/**
 * Extracts crop water layers from cropWaterData if data is avaible and user has the product
 * @param {Object} seasonObject
 * @param {Object} cropWaterData
 * @param {Array<String>} productAssets
 * @return {Object}
 */
export function extractCropWaterLayers(
  seasonObject,
  cropWaterData,
  productAssets
) {
  const { year: seasonYear } = seasonObject;
  const cropWaterDates = getLayerDatesBySeason(cropWaterData, seasonYear);
  if (cropWaterDates.length && hasCropWaterUse(productAssets)) {
    return {
      [CROP_WATER_KEY]: {
        byId: cropWaterDates.reduce(
          (events, date) => ({
            ...events,
            [date]: parseCropWaterUseEvent(cropWaterData[date]),
          }),
          {}
        ),
        allIds: sortDatesDescending(cropWaterDates),
        current: [cropWaterDates[0], cropWaterDates[0]],
      },
    };
  }
  return {};
}

/**
 * Extracts an array of events into an object where the dates of each event are the keys
 * @param {Array<Object>} events
 * @return {Object}
 */
export function extractCropWaterUseEvents(events) {
  return events.reduce(
    (orderedEvents, event) => ({
      ...orderedEvents,
      [event.eventDate]: event,
    }),
    {}
  );
}

/**
 * Parses the legend of the crop water use event
 * @param {Object} legend
 * @return {Object}
 */
export function parseCropWaterUseLegend(legend) {
  const intervals = Object.keys(legend).reduce(
    (eventIntervals, intervalKey) => {
      const convertedValue = createUnit(
        legend[intervalKey].value.q,
        legend[intervalKey].value.u
      ).forLocaleGroup(LOCALE_GROUPS.CROP_WATER_USE).scalarLocaleString;
      return [
        { hex: legend[intervalKey].color, stringValue: convertedValue },
        ...eventIntervals,
      ];
    },
    []
  );
  const labelHigh = inferUnit(LOCALE_GROUPS.CROP_WATER_USE).symbol.universal;
  return { intervals, labelHigh: `${labelHigh}/${getMessage('legend.day')}` };
}
